import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { ConboxDialogComponent } from '../../../../../shared/components/add-file/dialog-conbox.component';
import { LocalDB } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  EMPTY_GUID,
  findOption,
  fromApiDate,
  isBlank,
  numberOrNull,
  optionId,
  serverErrorMessage,
  toApiDate,
} from '../../../../finance/entity-form/entity-form.utils';
import { FinancialInvestmentFormDialogData, FinancialInvestmentRecord } from '../../models/financial-investment.model';
import { FinancialInvestmentEntityService } from '../../services/financial-investment-entity.service';
import { FinancialInvestmentAccountOption, FinancialInvestmentLookupsFacade } from '../../services/financial-investment-lookups.facade';

type FinancialInvestmentFormErrors = Partial<Record<
  'account' | 'investmentValue' | 'investmentDate' | 'investmentDescription' | 'numberOfUnitsOrSharesInvested'
  | 'percentageOfProfitabilityOrInterest' | 'termInMonths' | 'netBookValue', string>>;

export const FINANCIAL_DESCRIPTION_MAX_LENGTH = 150;
const MAX_MONEY = 99999999999999999;
const MAX_UNITS = 9999999.99;
const MAX_PERCENTAGE = 999.99;
const MAX_TERM = 255;

@Component({
  selector: 'app-financial-investment-form-dialog',
  templateUrl: './financial-investment-form-dialog.component.html',
  styleUrls: ['../../../../finance/entity-form/entity-form-dialog.scss', './financial-investment-form-dialog.component.scss'],
  standalone: false,
})
export class FinancialInvestmentFormDialogComponent implements OnInit, OnDestroy {
  readonly descriptionMaxLength = FINANCIAL_DESCRIPTION_MAX_LENGTH;

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: FinancialInvestmentFormErrors = {};

  accounts: FinancialInvestmentAccountOption[] = [];

  fileUid: string | null = null;
  isImage = false;
  isPdf = false;
  loadingFile = false;
  imageUrl: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  private loaded: FinancialInvestmentRecord | null = null;
  private objectUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: FinancialInvestmentFormDialogData): Observable<boolean> {
    return dialog.open<FinancialInvestmentFormDialogComponent, FinancialInvestmentFormDialogData, boolean>(FinancialInvestmentFormDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<FinancialInvestmentFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: FinancialInvestmentFormDialogData,
    private entity: FinancialInvestmentEntityService,
    private lookups: FinancialInvestmentLookupsFacade,
    private addFileService: AddFileService,
    private sanitizer: DomSanitizer,
    private notify: NotificationService,
  ) {
    this.form = this.fb.group({
      account: [null],
      investmentValue: [null],
      investmentDate: [null],
      investmentDescription: [''],
      numberOfUnitsOrSharesInvested: [null],
      percentageOfProfitabilityOrInterest: [null],
      termInMonths: [null],
      netBookValue: [null],
      lastBalanceSheetDate: [null],
      generatesReturns: [false],
    });
  }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar inversión financiera' : 'Nueva inversión financiera';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return [this.loaded.investmentDescription, this.loaded.accountingModelsNumber].filter(Boolean).join(' · ') || 'Modifica los datos de la inversión';
    }
    return 'Registra una inversión en bonos, depósitos, fondos u otros activos financieros';
  }

  get accountControl(): UntypedFormControl {
    return this.form.get('account') as UntypedFormControl;
  }

  get investmentValueControl(): UntypedFormControl {
    return this.form.get('investmentValue') as UntypedFormControl;
  }

  get investmentDateControl(): UntypedFormControl {
    return this.form.get('investmentDate') as UntypedFormControl;
  }

  get investmentDescriptionControl(): UntypedFormControl {
    return this.form.get('investmentDescription') as UntypedFormControl;
  }

  get numberOfUnitsControl(): UntypedFormControl {
    return this.form.get('numberOfUnitsOrSharesInvested') as UntypedFormControl;
  }

  get percentageControl(): UntypedFormControl {
    return this.form.get('percentageOfProfitabilityOrInterest') as UntypedFormControl;
  }

  get termInMonthsControl(): UntypedFormControl {
    return this.form.get('termInMonths') as UntypedFormControl;
  }

  get netBookValueControl(): UntypedFormControl {
    return this.form.get('netBookValue') as UntypedFormControl;
  }

  get lastBalanceSheetDateControl(): UntypedFormControl {
    return this.form.get('lastBalanceSheetDate') as UntypedFormControl;
  }

  get generatesReturnsControl(): UntypedFormControl {
    return this.form.get('generatesReturns') as UntypedFormControl;
  }

  get hasFile(): boolean {
    return !!this.fileUid;
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    const pdf = file.type === 'application/pdf' || name.endsWith('.pdf');
    const image = /\.(png|jpe?g)$/.test(name) || file.type === 'image/png' || file.type === 'image/jpeg';
    if (!pdf && !image) {
      this.notify.warning('Solo se admiten archivos PDF, PNG o JPG.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    this.loadingFile = true;
    this.addFileService.attachFile(formData, consts.INVESTMENT_ENTITY)
      .then(response => {
        if (!response || response.error) {
          this.notify.error(response && response.message ? response.message : 'No se pudo subir el archivo.');
          return;
        }
        LocalDB.RemoveForFiles();
        this.fileUid = response.uid;
        this.isPdf = pdf;
        this.isImage = !pdf;
        this.showPreview(file);
      })
      .catch(error => this.notify.error(serverErrorMessage(error, 'No se pudo subir el archivo.')))
      .finally(() => this.loadingFile = false);
  }

  pickFromFiles(): void {
    this.dialog.open(ConboxDialogComponent, {
      width: '80%',
      height: '80%',
      maxWidth: '96vw',
      data: { originEntity: consts.INVESTMENT_ENTITY, filePath: consts.INVESTMENT_FILE_PATH }
    }).afterClosed().subscribe(result => {
      if (!result || !result.uid) return;

      const pdf = result.type === consts.TYPE_FILE_PDF;
      const image = result.type === consts.TYPE_FILE_IMAGE;
      if (!pdf && !image) {
        this.notify.warning('Solo se admiten documentos PDF o imágenes.');
        return;
      }

      this.loadingFile = true;
      this.addFileService.getImageFromFiles(result.uid, consts.INVESTMENT_ENTITY)
        .then(blob => {
          this.fileUid = result.uid;
          this.isPdf = pdf;
          this.isImage = image;
          this.showPreview(blob);
        })
        .catch(() => this.notify.error('No se pudo cargar el documento seleccionado.'))
        .finally(() => this.loadingFile = false);
    });
  }

  removeFile(): void {
    this.fileUid = null;
    this.isImage = false;
    this.isPdf = false;
    this.revokePreview();
  }

  save(): void {
    if (this.saving || this.loadingFile) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isEdit ? this.entity.update(this.data.uid as string, payload) : this.entity.create(payload);

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Inversión financiera actualizada correctamente' : 'Inversión financiera creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, this.isEdit
          ? 'Ha ocurrido un error inesperado al actualizar la inversión'
          : 'Ha ocurrido un error inesperado al crear la inversión');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<FinancialInvestmentRecord | null> = this.isEdit ? this.entity.get(this.data.uid as string) : Promise.resolve(null);

    Promise.all([record, this.lookups.accounts()])
      .then(([investment, accounts]) => {
        this.accounts = accounts;
        this.loaded = investment;
        if (investment) this.applyRecord(investment);
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(investment: FinancialInvestmentRecord): void {
    this.form.patchValue({
      account: this.accountOption(investment),
      investmentValue: numberOrNull(investment.investmentValue),
      investmentDate: fromApiDate(investment.investmentDate),
      investmentDescription: investment.investmentDescription || '',
      numberOfUnitsOrSharesInvested: numberOrNull(investment.numberOfUnitsOrSharesInvested),
      percentageOfProfitabilityOrInterest: numberOrNull(investment.percentageOfProfitabilityOrInterest),
      termInMonths: numberOrNull(investment.termInMonths),
      netBookValue: numberOrNull(investment.netBookValue),
      lastBalanceSheetDate: fromApiDate(investment.lastBalanceSheetDate),
      generatesReturns: investment.generatesReturns === true,
    }, { emitEvent: false });

    const fileUid = investment.filesInvestmentUid && investment.filesInvestmentUid !== EMPTY_GUID ? investment.filesInvestmentUid : null;
    this.fileUid = fileUid;
    this.isImage = !!fileUid && investment.isImage === true;
    this.isPdf = !!fileUid && investment.isPdf === true;
    if (fileUid && (this.isImage || this.isPdf)) {
      this.loadingFile = true;
      this.addFileService.getImageFromFiles(fileUid, consts.INVESTMENT_ENTITY)
        .then(blob => this.showPreview(blob))
        .catch(() => this.notify.warning('No se pudo cargar la vista previa del documento.'))
        .finally(() => this.loadingFile = false);
    }
  }

  private validate(): FinancialInvestmentFormErrors {
    const value = this.form.getRawValue();
    const errors: FinancialInvestmentFormErrors = {};

    if (optionId(value.account) === null || !this.selectedAccount(value.account)) {
      errors.account = 'Selecciona el tipo de inversión';
    }

    const investmentValue = numberOrNull(value.investmentValue);
    if (investmentValue === null) errors.investmentValue = 'Indica el valor de la inversión';
    else if (investmentValue <= 0) errors.investmentValue = 'El valor de la inversión debe ser mayor que 0';
    else if (investmentValue > MAX_MONEY) errors.investmentValue = 'Importe demasiado alto';

    if (!value.investmentDate) errors.investmentDate = 'Indica la fecha de la inversión';
    if (isBlank(value.investmentDescription)) errors.investmentDescription = 'Indica la descripción';
    else if (String(value.investmentDescription).trim().length > FINANCIAL_DESCRIPTION_MAX_LENGTH) errors.investmentDescription = `Máximo ${FINANCIAL_DESCRIPTION_MAX_LENGTH} caracteres`;

    const units = this.optionalNumber(value.numberOfUnitsOrSharesInvested);
    if (units === undefined || (units !== null && (units < 0 || units > MAX_UNITS))) {
      errors.numberOfUnitsOrSharesInvested = `Indica un número entre 0 y ${MAX_UNITS.toLocaleString('es-ES')}`;
    }

    const percentage = this.optionalNumber(value.percentageOfProfitabilityOrInterest);
    if (percentage === undefined || (percentage !== null && (percentage < 0 || percentage > MAX_PERCENTAGE))) {
      errors.percentageOfProfitabilityOrInterest = `Indica un porcentaje entre 0 y ${MAX_PERCENTAGE.toLocaleString('es-ES')}`;
    }

    const term = this.optionalNumber(value.termInMonths);
    if (term === undefined || (term !== null && (!Number.isInteger(term) || term < 0 || term > MAX_TERM))) {
      errors.termInMonths = `Indica un número entero de meses entre 0 y ${MAX_TERM}`;
    }

    const net = this.optionalNumber(value.netBookValue);
    if (net === undefined || (net !== null && Math.abs(net) > MAX_MONEY)) {
      errors.netBookValue = 'Indica un importe válido';
    }

    return errors;
  }

  private optionalNumber(value: any): number | null | undefined {
    if (isBlank(value)) return null;
    const number = Number(value);
    return isNaN(number) ? undefined : number;
  }

  private selectedAccount(option: any): FinancialInvestmentAccountOption | null {
    return findOption(this.accounts, optionId(option)) as FinancialInvestmentAccountOption | null;
  }

  private accountOption(investment: FinancialInvestmentRecord): FinancialInvestmentAccountOption | null {
    const number = investment.accountingModelsNumber;
    const existing = findOption(this.accounts, number) as FinancialInvestmentAccountOption | null;
    if (existing || !number) return existing;
    const missing: FinancialInvestmentAccountOption = {
      id: number,
      display: number,
      accountingModelsId: Number(investment.accountingModelsId) || 0,
      number,
      name: '',
    };
    this.accounts = [...this.accounts, missing];
    return missing;
  }

  private buildPayload(): FinancialInvestmentRecord {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const account = this.selectedAccount(value.account) as FinancialInvestmentAccountOption;
    const round = (n: number) => Math.round(n * 100) / 100;

    return {
      ...base,
      accountingModelsNumber: account.number,
      accountingModelsId: account.accountingModelsId,
      filesInvestmentUid: this.fileUid || EMPTY_GUID,
      isImage: this.isImage,
      isPdf: this.isPdf,
      investmentValue: round(Number(value.investmentValue)),
      investmentDate: toApiDate(value.investmentDate),
      investmentDescription: String(value.investmentDescription).trim(),
      numberOfUnitsOrSharesInvested: round(Number(this.optionalNumber(value.numberOfUnitsOrSharesInvested) || 0)),
      percentageOfProfitabilityOrInterest: round(Number(this.optionalNumber(value.percentageOfProfitabilityOrInterest) || 0)),
      termInMonths: Number(this.optionalNumber(value.termInMonths) || 0),
      netBookValue: round(Number(this.optionalNumber(value.netBookValue) || 0)),
      lastBalanceSheetDate: toApiDate(value.lastBalanceSheetDate),
      generatesReturns: value.generatesReturns === true,
      paided: base.paided || '',
      manualMovement: null,
    };
  }

  private showPreview(blob: Blob): void {
    this.revokePreview();
    if (!blob) return;
    const typed = this.isPdf && blob.type !== 'application/pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
    this.objectUrl = URL.createObjectURL(typed);
    if (this.isPdf) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } else {
      this.imageUrl = this.objectUrl;
    }
  }

  private revokePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.imageUrl = null;
    this.pdfUrl = null;
  }
}
